// (웹/앱) 마이 링글 > AI 분석 통계 - 추천 학습 영역
import { QIcon_chevron_right } from '@repo/icons/index'
import { useCafSummaryHighlightsV5 } from '../../../../modules/apis/webview/caf'
import { getRoundValue } from '../../../../modules/hooks/webview/caf/chart/utilFunction'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import LoadingSpinner from '../../../common/LoadingSpinner'
import HighSumCardTemplate from '../highlight/HighSumCardTemplate'
import SimpleBarChart from './SimpleBarChart'

import classNames from 'classnames'
import { useCallback } from 'react'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useMapTagToWording from '../../../../modules/hooks/webview/caf/common/useMapTagToWording'
import useWebviewNavigation from '../../../../modules/hooks/webview/common/useWebviewNavigation'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { LESSON_ACTIVITIES_MAP, useLessonsStack } from '../../../../pages/[lang]/webview/lessons'

interface IRecommHighLightSectionProps {
  startDate: string
  endDate: string
  count: number
}

const RecommHighlightSection = ({ startDate, endDate, count }: IRecommHighLightSectionProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { data: dataList, isLoading } = useCafSummaryHighlightsV5({
    startDate,
    endDate,
    pinned: false,
    recommended: true,
  })
  const isWebview = useIsWebview()
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const router = useAppRouter()
  const stripActiveData = useCallback((dtList) => dtList.filter((dataItem) => dataItem.average_count > 0), [])
  const sliceThree = useCallback((arr) => (arr?.length > 3 ? arr.slice(0, 3) : arr), [])
  const { currentUser } = useCurrentUser()
  const { navigate } = useWebviewNavigation()
  const goToDetailPage = (start, end) => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightListActivity, {
        initialStartDate: start,
        initialEndDate: end,
        edit: false,
      })
    } else {
      router.push(urls.student.newPortal.myRingle.AIAnalysis.asTab(2))
    }
  }
  const summGraphData = useCallback((highlights) => {
    return highlights?.slice(0, 14).map((item, idx) => {
      return {
        ...item,
        color: COLORS.GRAY300,
        turn: idx,
      }
    })
  }, [])

  const { countWordingPerTag, namePerTag, iconPerTag } = useMapTagToWording()
  const handleClickCard = (tagId, startDate, endDate) => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightDetailActivity, {
        tagId: tagId,
        startDate,
        endDate,
      })
    } else if (isMobile) {
      const url = urls.webview.caf.statistic.highlight.tag(tagId, startDate, endDate)
      navigate({ url })
    } else {
      router.push(urls.student.newPortal.myRingle.AIAnalysis.asTag(2, startDate, endDate, tagId))
    }
  }

  const shownCount = useCallback((item) => item?.average_count, [])
  const noName = !currentUser?.user_info?.first_name && !currentUser?.user_info?.last_name
  if (isLoading || !dataList)
    return (
      <div className="flex h-[100px] w-full flex-row items-center justify-center">
        <LoadingSpinner show primary />
      </div>
    )
  if (isMobile) {
    return (
      <section className="py-[24px]">
        <div className={styles.title.box}>
          <div className={styles.title.text}>{t(n_caf.statistic.highlight_title)}</div>
          <div className={styles.title.subtitle}>
            {t(
              n_caf.statistic.highlight_detail(
                noName ? '고객' : currentUser?.user_info?.first_name ?? currentUser?.user_info?.last_name,
                count
              )
            )}
          </div>
        </div>
        {dataList.length > 0 ? (
          <ul>
            {sliceThree(stripActiveData(dataList))?.map((highItem, index) => {
              const Icon = iconPerTag(highItem.tag)
              return (
                <li key={index + 'recommended_highlight'}>
                  <HighSumCardTemplate
                    title={
                      <p className={styles.highItem.text}>
                        <Icon
                          width={18}
                          height={18}
                          className="mr-[2px] [&>*]:fill-current"
                          color={COLORS.PURPLE500}
                          fill={COLORS.PURPLE500}
                        />
                        {namePerTag(highItem.tag)}
                      </p>
                    }
                    subtitle={
                      shownCount(highItem) > 0
                        ? t(countWordingPerTag(highItem.tag, getRoundValue(shownCount(highItem))).count)
                        : t(countWordingPerTag(highItem.tag).noCount)
                    }
                    bottomSection={
                      shownCount(highItem) !== 0 ? (
                        <SimpleBarChart
                          data={summGraphData(highItem.counts)}
                          graphKey={'count'}
                          leftCount={<span className="!text-purple-500">{getRoundValue(highItem.average_count)}</span>}
                          rightCount={null}
                          marker={highItem.average_count}
                        />
                      ) : null
                    }
                    disabled={shownCount(highItem) === 0}
                    onClick={() => handleClickCard(highItem.tag, startDate, endDate)}
                  />
                </li>
              )
            })}
          </ul>
        ) : (
          <></>
        )}
        <div className={classNames('sm:hidden', styles.more.box)} onClick={() => goToDetailPage(startDate, endDate)}>
          <p className={styles.more.text}>{t(n_caf.overview.highlight_seeall)}</p>
          <QIcon_chevron_right color={COLORS.GRAY600} height={10} />
        </div>
      </section>
    )
  } else {
    return (
      <section>
        <div className={styles.title.box}>
          <div className="mb-[8px] flex w-full items-center justify-between">
            <div className={styles.title.text_pc}>{t(n_caf.statistic.highlight_title)}</div>
            <div
              className="flex cursor-pointer flex-row items-center"
              onClick={() => router.push(urls.student.newPortal.myRingle.AIAnalysis.asTab(2))}
            >
              <div className="mr-4 text-[16px] font-bold text-black">{t(n_caf.overview.highlight_seeall)}</div>
              <QIcon_chevron_right width={14} height={14} color={COLORS.GRAY600}></QIcon_chevron_right>
            </div>
          </div>
          <div className={styles.title.subtitle_pc}>
            {t(
              n_caf.statistic.highlight_detail(
                noName ? '고객' : currentUser?.user_info?.first_name ?? currentUser?.user_info?.last_name,
                count
              )
            )}
          </div>
        </div>
        {dataList.length > 0 ? (
          <div className="flex flex-col md:flex-row md:flex-wrap w-full gap-[16px]">
            {sliceThree(stripActiveData(dataList))?.map((highItem, index) => {
              const Icon = iconPerTag(highItem.tag)
              return (
                <HighSumCardTemplate
                  key={index + 'recommended_highlight'}
                  title={
                    <p className={styles.highItem.text}>
                      <Icon
                        width={18}
                        height={18}
                        className="mr-[2px] [&>*]:fill-current"
                        color={COLORS.PURPLE500}
                        fill={COLORS.PURPLE500}
                      />
                      {namePerTag(highItem.tag)}
                    </p>
                  }
                  subtitle={
                    shownCount(highItem) > 0
                      ? t(countWordingPerTag(highItem.tag, getRoundValue(shownCount(highItem))).count)
                      : t(countWordingPerTag(highItem.tag).noCount)
                  }
                  bottomSection={
                    shownCount(highItem) !== 0 ? (
                      <SimpleBarChart
                        data={summGraphData(highItem.counts)}
                        graphKey={'count'}
                        leftCount={<span className="!text-purple-500">{getRoundValue(highItem.average_count)}</span>}
                        rightCount={null}
                        marker={highItem.average_count}
                      />
                    ) : null
                  }
                  disabled={shownCount(highItem) === 0}
                  onClick={() => handleClickCard(highItem.tag, startDate, endDate)}
                  className="h-[188px] w-[336px]"
                />
              )
            })}
          </div>
        ) : (
          <></>
        )}
      </section>
    )
  }
}

export default RecommHighlightSection

const styles = {
  title: {
    box: `mb-[24px]`,
    text: `text-sTitle mb-[12px] font-bold text-black`,
    text_pc: `text-[20px] font-bold text-black`,
    subtitle: `text-[14px] font-medium text-black`,
    subtitle_pc: `text-[16px] font-medium text-gray-600`,
  },
  more: {
    box: `flex items-center justify-end gap-4 tracking-tight`,
    text: `h-[20px] text-right text-[14px] font-medium text-gray-500`,
  },
  highItem: { text: `text-h5 flex items-center gap-[4px] font-medium leading-none text-purple-500` },
}
